import dayjs from 'dayjs';
import { datePickerMinDateMaxDateValidate } from '../../../utils/dateUtil';
import { IfspClientDetailsType } from '../../../redux/features/ifsp/ifspClientDetailsSlice';

export type ValidationResult = {
  ifspFirstMeetingDate?: boolean;
  ifspFirstMeetingDateErrorMessageId?: string;
  ifspFirstMeetingDateErrorMessage?: string;
  ifspFirstMeetingDelay?: boolean;
  ifspFirstMeetingDelayErrorMessageId?: string;
  ifspFirstMeetingDelayErrorMessage?: string;
  ifspSignedDate?: boolean;
  ifspSignedDateErrorMessageId?: string;
  ifspSignedDateErrorMessage?: string;
  justification?: boolean;
  justificationErrorMessageId?: string;
  justificationErrorMessage?: string;
  reasonForDelay?: boolean;
  reasonForDelayErrorMessageId?: string;
  reasonForDelayErrorMessage?: string;
};

export const useIfspClientInfoValidationCheckToSubmit = (
  ifspClientDetails: IfspClientDetailsType,
): ValidationResult => {
  // Start with empty result
  const validationResult: ValidationResult = {};

  //  =============== IFSP First Meeting Date ===============
  if (!ifspClientDetails.ifspFirstMeetingDate) {
    Object.assign(validationResult, {
      ifspFirstMeetingDate: true,
      ifspFirstMeetingDateErrorMessageId:
        'IFSP.ClientDetails.FirstMeetingDateRequired',
      ifspFirstMeetingDateErrorMessage: 'IFSP first meeting date is required',
    });
  } else if (
    ifspClientDetails.ifspFirstMeetingDate &&
    dayjs(ifspClientDetails.ifspFirstMeetingDate).isAfter(
      dayjs(ifspClientDetails.client.dateOfBirth).add(3, 'year'),
      'day',
    )
  ) {
    Object.assign(validationResult, {
      ifspFirstMeetingDate: true,
      ifspFirstMeetingDateErrorMessageId:
        'IFSP.ClientDetails.FirstMeetingDateCannotBeMoreThan3Years',
      ifspFirstMeetingDateErrorMessage:
        'IFSP first meeting date cannot be after 3rd birthday',
    });
  } else if (
    dayjs(ifspClientDetails.ifspFirstMeetingDate, 'MM/DD/YYYY').isAfter(dayjs())
  ) {
    Object.assign(validationResult, {
      ifspFirstMeetingDate: true,
      ifspFirstMeetingDateErrorMessageId:
        'IFSP.ClientDetails.FirstMeetingCannotBeFuture',
      ifspFirstMeetingDateErrorMessage:
        'IFSP first meeting cannot be future date',
    });
  } else if (
    datePickerMinDateMaxDateValidate(ifspClientDetails.ifspFirstMeetingDate)
  ) {
    Object.assign(validationResult, {
      ifspFirstMeetingDate: true,
      ifspFirstMeetingDateErrorMessageId: 'datePickerMinDateMaxDateValidate',
      ifspFirstMeetingDateErrorMessage: 'IFSP Meeting Date is invalid',
    });
  } else if (
    dayjs(ifspClientDetails.ifspFirstMeetingDate, 'MM/DD/YYYY').isBefore(
      dayjs(ifspClientDetails.initialEvalNotificationDate, 'MM/DD/YYYY'),
    )
  ) {
    Object.assign(validationResult, {
      ifspFirstMeetingDate: true,
      ifspFirstMeetingDateErrorMessageId:
        'IFSP.ClientDetails.FirstMeetingDateBeforeInitialEvalNotificationDate',
      ifspFirstMeetingDateErrorMessage:
        "IFSP Meeting Date can't be before Initial Evaluation Notification Date",
    });
  }

  //  ===============IFSP Signed Date Validation================
  if (
    ifspClientDetails.ifspSignedDate &&
    dayjs(ifspClientDetails.ifspSignedDate, 'MM/DD/YYYY').isBefore(
      dayjs(ifspClientDetails.ifspFirstMeetingDate, 'MM/DD/YYYY'),
    )
  ) {
    Object.assign(validationResult, {
      ifspSignedDate: true,
      ifspSignedDateErrorMessageId:
        'IFSP.ClientDetails.IfspSignedDateBeforeIfspFirstMeetingDate',
      ifspSignedDateErrorMessage:
        "IFSP Signed Date can't be before IFSP First Meeting Date",
    });
  } else if (!ifspClientDetails.ifspSignedDate && ifspClientDetails.tbrIfspId) {
    Object.assign(validationResult, {
      ifspSignedDate: true,
      ifspSignedDateErrorMessageId: 'IFSP.ClientDetails.IfspSignedDateRequired',
      ifspSignedDateErrorMessage: 'IFSP Signed Date is required',
    });
  } else if (
    ifspClientDetails.ifspSignedDate &&
    datePickerMinDateMaxDateValidate(ifspClientDetails.ifspSignedDate)
  ) {
    Object.assign(validationResult, {
      ifspSignedDate: true,
      ifspSignedDateErrorMessageId: 'datePickerMinDateMaxDateValidate',
      ifspSignedDateErrorMessage: 'IFSP Signed Date is invalid',
    });
  }

  //  ===============Justification Validation================

  if (
    ifspClientDetails.primarySettingCode &&
    ifspClientDetails.primarySettingCode !== 'HOME' &&
    ifspClientDetails.primarySettingCode !== 'COMMUNITY' &&
    !ifspClientDetails.justification
  ) {
    Object.assign(validationResult, {
      justification: true,
      justificationErrorMessageId: 'IFSP.ClientDetails.JustificationRequired',
      justificationErrorMessage: 'Justification is required',
    });
  }

  //  ===============IFSP First Meeting Delay Validation================

  if (
    dayjs(ifspClientDetails.ifspFirstMeetingDate, 'MM/DD/YYYY').isAfter(
      dayjs(ifspClientDetails.ifspDueDate, 'MM/DD/YYYY'),
    ) &&
    !ifspClientDetails.reasonForDelay
  ) {
    Object.assign(validationResult, {
      reasonForDelay: true,
      reasonForDelayErrorMessageId: 'IFSP.ClientDetails.ReasonForDelayRequired',
      reasonForDelayErrorMessage: `Reason for delay is required since the IFSP First Meeting Date is after the due date (${ifspClientDetails.ifspDueDate})`,
    });
  }

  return validationResult;
};
